import React, { useState, useEffect, useRef } from 'react';
import { BarChart2 } from 'lucide-react';
import { useTheme } from '../core/ui/ThemeContext';
import { useLanguage } from '../core/i18n/LanguageContext';
import { getLagerDashboard, getLagerWerke } from 'core/api/apiService';
import KpiCards from './lager/dashboard/KpiCards';
import BestandsverlaufChart from './lager/dashboard/BestandsverlaufChart';
import DurchlaufzeitenChart from './lager/dashboard/DurchlaufzeitenChart';
import TopMaterials from './lager/dashboard/TopMaterials';
import Aktivitaeten from './lager/dashboard/Aktivitaeten';
import WerkDropdown from './lager/WerkDropdown';

/**
 * LagerDashboard — Monitoring-/KPI-Dashboard (Phase 4 / Dashboard-v2).
 * Props:
 *   user          Object   (aus useAuth)
 *   selectedWerk  string   geteiltes Werk (von LagerShell gesteuert)
 *   onWerkChange  (werk: string) => void
 *   onDrilldown   (filter: object) => void   KPI-Karte angeklickt -> Shell wechselt zu Lagerbestand
 */
const LagerDashboard = ({ user, selectedWerk, onWerkChange, onDrilldown }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  // ---------- Werke (fuer Dropdown) ----------
  const [werke, setWerke] = useState([]);
  const [werkOpen, setWerkOpen] = useState(false);
  const werkRef = useRef(null);

  // ---------- Dashboard-Daten ----------
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ---------- Werke laden (nur einmal, Shell haelt das aktuelle Werk) ----------
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await getLagerWerke(user);
        if (cancelled) return;
        const list = Array.isArray(res?.werke) ? res.werke : [];
        setWerke(list);
        // Nur setzen wenn Shell noch kein Werk hat
        if (!selectedWerk && typeof onWerkChange === 'function') {
          const def = res?.default_werk || (list.length > 0 ? list[0].werk : '');
          if (def) onWerkChange(def);
        }
      } catch {
        // Werke-Fehler nicht kritisch
      }
    };
    load();
    return () => { cancelled = true; };
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- Dashboard-Daten laden ----------
  useEffect(() => {
    if (!selectedWerk) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await getLagerDashboard(user, selectedWerk);
        if (cancelled) return;
        setData(res);
      } catch (err) {
        if (!cancelled) setError(err.message || t('lm.dashError'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [selectedWerk, user]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- Werke-Dropdown schliessen ----------
  useEffect(() => {
    const handle = (e) => {
      if (werkRef.current && !werkRef.current.contains(e.target)) setWerkOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const handleWerkChange = (werk) => {
    setWerkOpen(false);
    if (typeof onWerkChange === 'function') onWerkChange(werk);
  };

  // ---- Styles ----
  const bgCls = isDarkMode
    ? 'bg-gradient-to-br from-slate-900 via-gray-900 to-slate-800'
    : 'bg-slate-50';

  const headerBg = isDarkMode
    ? 'bg-gray-900/20 border-b border-white/10 backdrop-blur-2xl shadow-[0_8px_32px_rgba(0,0,0,0.3)]'
    : 'bg-white border-b border-slate-200';

  const iconBoxCls = isDarkMode
    ? 'bg-gradient-to-br from-slate-600/60 via-gray-600/50 to-slate-700/60 border border-white/20 shadow-[0_6px_24px_rgba(0,0,0,0.25),inset_0_1px_0_rgba(255,255,255,0.3)]'
    : 'bg-brand/10';

  const titleCls = isDarkMode
    ? 'bg-gradient-to-r from-white via-blue-200 to-white bg-clip-text text-transparent text-xl font-semibold'
    : 'text-slate-900 text-xl font-semibold';

  const subtitleCls = isDarkMode ? 'text-white/50 text-xs mt-0.5' : 'text-slate-500 text-xs mt-0.5';

  // ---- Render ----
  return (
    <div className={`flex-1 flex flex-col min-h-0 ${bgCls}`}>

      {/* ---- Header ---- */}
      <div className="relative flex-shrink-0">
        <div className={`absolute inset-0 ${headerBg}`} />
        <div className="relative max-w-full px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4 flex-wrap">

            {/* Icon + Titel */}
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBoxCls}`}>
                <BarChart2 className={`w-5 h-5 ${isDarkMode ? 'text-white/90' : 'text-brand'}`} />
              </div>
              <div>
                <h1 className={titleCls}>{t('lm.dashTitle')}</h1>
                <p className={subtitleCls}>{t('lm.subtitle')}</p>
              </div>
            </div>

            {/* Werksauswahl */}
            <div className="ml-auto">
              <WerkDropdown
                werke={werke}
                selectedWerk={selectedWerk || ''}
                onWerkChange={handleWerkChange}
                loading={werke.length === 0 && !selectedWerk}
                werkOpen={werkOpen}
                setWerkOpen={setWerkOpen}
                werkRef={werkRef}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ---- Content ---- */}
      <div className="flex-1 overflow-auto px-4 sm:px-6 lg:px-8 py-6">

        {/* Loading */}
        {loading && (
          <div className={`flex items-center justify-center py-16 ${isDarkMode ? 'text-white/50' : 'text-slate-400'}`}>
            <span className="text-sm">{t('lm.dashLoading')}</span>
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className={`rounded-2xl p-5 text-sm ${isDarkMode ? 'bg-red-500/15 text-red-300 border border-red-500/25' : 'bg-red-50 text-red-600 border border-red-200'}`}>
            {t('lm.dashError')}: {error}
          </div>
        )}

        {/* Empty */}
        {!loading && !error && !data && (
          <div className={`flex items-center justify-center py-16 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>
            <span className="text-sm">{t('lm.dashEmpty')}</span>
          </div>
        )}

        {/* Daten vorhanden */}
        {!loading && !error && data && (
          <div className="flex flex-col gap-6 max-w-6xl mx-auto">
            {/* KPI-Karten (klickbar) */}
            <KpiCards kpis={data.kpis} onDrilldown={onDrilldown} />

            {/* Bestandsverlauf */}
            <BestandsverlaufChart bestandsverlauf={data.bestandsverlauf} />

            {/* Durchlaufzeiten + Top Materialien */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <DurchlaufzeitenChart durchlaufzeiten={data.durchlaufzeiten} />
              <TopMaterials top_materialien={data.top_materialien} />
            </div>

            {/* Letzte Aktivitaeten */}
            <Aktivitaeten aktivitaeten={data.aktivitaeten} />
          </div>
        )}
      </div>
    </div>
  );
};

export default LagerDashboard;
